import { parseMarkdownLinks } from '../markdown/parseMarkdownLinks'
import { rewriteNoteLinks } from '../markdown/rewriteNoteLinks'
import { rewriteAttachmentLinks } from '../markdown/rewriteAttachmentLinks'
import { applyBearTransforms } from '../markdown/bearTransforms'
import { detectCraftExport, applyCraftTransforms } from '../markdown/craftTransforms'
import { parseObsidianSettings, applyObsidianTransforms } from '../markdown/obsidianTransforms'
import { stripFirstDirectory } from '../path'
import { sanitizeFilename, makeUniqueFilename } from './filenameSanitizer'
import {
  shouldSkipFile,
  isMarkdownFile,
  isAttachmentFile,
  detectCalendarFromFilename,
  isBinaryContent,
  type ImportNote,
  type ImportItem,
  type ImportBatchResult,
  type ImportWarning,
  type ImportHint,
  type ImportSourceType,
} from './core'

/**
 * Reads file content using streaming approach to reduce memory usage
 * Processes file in 64KB chunks to avoid loading large files entirely into memory
 *
 * @param file - The file to read
 * @param chunkSize - Size of each chunk in bytes (default: 64KB)
 * @returns Promise resolving to the complete file content as string
 */
async function readFileStreamingContent(file: File, chunkSize = 64 * 1024): Promise<string> {
  const chunks: string[] = []
  let offset = 0

  while (offset < file.size) {
    const chunkBlob = file.slice(offset, offset + chunkSize)
    const chunkText = await chunkBlob.text()
    chunks.push(chunkText)
    offset += chunkSize
  }

  return chunks.join('')
}

/**
 * Adds folder hierarchy to the folders set
 * Helper function for building complete folder structure from file paths
 */
function addFolderHierarchy(folders: Set<string>, folderPath: string) {
  const segments = folderPath.split('/')
  let currentPath = ''

  for (const segment of segments) {
    currentPath = currentPath ? `${currentPath}/${segment}` : segment
    folders.add(currentPath)
  }
}

/**
 * Builds import batch from FileList, categorizing folders, notes, and attachments
 *
 * @param files - The FileList to process
 * @param sourceType - The source type of the import ('craft', 'obsidian', 'markdown', 'bear', or 'auto')
 *                     Defaults to 'auto' for backward compatibility and auto-detection
 *
 * PERFORMANCE: Uses sequential processing for simplicity and memory safety.
 */
export async function buildImportBatch(
  files: FileList,
  sourceType: ImportSourceType = 'auto'
): Promise<ImportBatchResult> {
  const folders = new Set<string>()
  const notes: ImportNote[] = []
  const attachmentFiles = new Map<string, File>()
  const usedAttachmentNames = new Set<string>()
  const warnings: ImportWarning[] = []
  const hints: ImportHint[] = []

  // Detect Craft export once at the start
  const isCraftExport = detectCraftExport(files)

  // Parse Obsidian settings from .obsidian/app.json if present
  const obsidianSettings = await parseObsidianSettings(files)

  // Determine effective source type
  let effectiveSourceType = sourceType
  if (sourceType === 'auto') {
    if (isCraftExport) {
      effectiveSourceType = 'craft'
    } else if (obsidianSettings) {
      effectiveSourceType = 'obsidian'
    } else {
      effectiveSourceType = 'markdown'
    }
  }

  // First pass: catalog all files
  for (const file of files) {
    const raw = file.webkitRelativePath || file.name
    const path = stripFirstDirectory(raw)

    // Skip hidden files, system files
    if (shouldSkipFile(path)) {
      // eslint-disable-next-line no-console -- Intentional error logging for debugging
      console.error('buildImportBatch: Skipped binary/system/hidden file', {
        path,
      })
      continue
    }

    // Categorize files
    if (isMarkdownFile(path)) {
      // Collect folders from NOTE paths only (not attachment paths)
      // This prevents .assets folders from being created in Craft imports
      const lastSlashIndex = path.lastIndexOf('/')
      if (lastSlashIndex > 0) {
        const folderPath = path.slice(0, lastSlashIndex)
        addFolderHierarchy(folders, folderPath)
      }
      // For markdown files, check for binary content masquerading as text
      const sampleBuffer = await file.slice(0, 16_384).arrayBuffer()
      if (isBinaryContent(sampleBuffer)) {
        warnings.push({
          path,
          message: `Binary file '${file.name}' cannot be imported as text - skipped`,
        })
        // eslint-disable-next-line no-console -- Intentional error logging for debugging
        console.error('buildImportBatch: Skipped binary file', {
          path,
          filename: file.name,
        })
        continue
      }
      
      notes.push({
        path,
        file,
        isCalendar: detectCalendarFromFilename(path),
        attachments: [], // Will be populated in second pass
      })
    } else if (isAttachmentFile(path)) {
      // For non-markdown files, let isAttachmentFile() determine validity
      // Binary files like images, PDFs etc. should be collected as potential attachments

      // Sanitize filename for cross-platform compatibility
      // Removes special characters that cause issues with native apps
      const sanitizedName = sanitizeFilename(file.name)

      // Ensure uniqueness across all attachment filenames
      const uniqueName = makeUniqueFilename(sanitizedName, usedAttachmentNames)
      usedAttachmentNames.add(uniqueName)

      // Create new File with sanitized/unique name if it changed
      const outFile = uniqueName === file.name
        ? file
        : new File([file], uniqueName, { type: file.type, lastModified: file.lastModified })

      attachmentFiles.set(path, outFile)
    }
  }

  // Second pass: scan markdown content for attachment links and detect missing references
  const referencedAttachmentPaths = new Set<string>()

  // Process notes sequentially for simplicity and memory safety
  for (const note of notes) {
    try {
      const content = await readFileStreamingContent(note.file)

      // Apply Bear transforms first (idempotent, safe for all content)
      let transformed = applyBearTransforms(content)

      // Apply source-specific transforms based on detected/specified source type
      switch (effectiveSourceType) {
        case 'craft': {
          transformed = applyCraftTransforms(transformed, {
            path: note.path,
            isCalendar: note.isCalendar,
          })
          break
        }

        case 'obsidian': {
          transformed = applyObsidianTransforms(transformed, {
            path: note.path,
            isCalendar: note.isCalendar,
            settings: obsidianSettings,
            attachmentIndex: new Set(attachmentFiles.keys()),
          })
          break
        }

        case 'markdown':
        case 'bear': {
          // No source-specific transforms for generic markdown or Bear exports
          // Bear transforms already applied above
          break
        }
      }

      // Parse all links and classify them by type
      const linkInfos = parseMarkdownLinks(transformed, note.path)

      // Filter for attachment links only (for validation)
      const attachmentLinks = linkInfos.filter((l) => l.kind === 'attachment')
      const referencedPaths = attachmentLinks.map((l) => l.resolved)

      // Track which attachment files are referenced
      for (const referencePath of referencedPaths) {
        referencedAttachmentPaths.add(referencePath)
      }

      // Detect missing attachment references
      for (const referencePath of referencedPaths) {
        if (!attachmentFiles.has(referencePath)) {
          warnings.push({
            path: note.path,
            message: `Attachment '${referencePath}' referenced but not found in import files`,
          })
        }
      }

      // Only include attachments that actually exist
      note.attachments = referencedPaths
        .map((referencePath) => attachmentFiles.get(referencePath))
        .filter(Boolean) as File[]

      // Rewrite attachment links to use only decoded basenames (removing .assets paths and URL encoding)
      // This is critical for Supabase where the editor matches attachments by filename
      const withAttachmentsRewritten = rewriteAttachmentLinks(transformed, note.path, {
        basenameResolver: (resolved) => attachmentFiles.get(resolved)?.name,
      })

      // Rewrite content to convert note links to wiki links
      const updatedContent = rewriteNoteLinks(withAttachmentsRewritten, note.path)

      // Create a new File object if content was changed by transforms or rewriting
      if (transformed !== content || updatedContent !== transformed) {
        const updatedFile = new File([updatedContent], note.file.name, {
          type: note.file.type,
          lastModified: note.file.lastModified,
        })
        note.file = updatedFile
      }
    } catch (error) {
      // eslint-disable-next-line no-console -- Intentional error logging for debugging
      console.error('buildImportBatch: Failed to read note content', {
        path: note.path,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // Third pass: generate hints for unused attachment files
  for (const [attachmentPath, file] of attachmentFiles) {
    if (!referencedAttachmentPaths.has(attachmentPath)) {
      // Skip hints for .assets files in Craft exports (they are internal organization files)
      if (isCraftExport && attachmentPath.includes('.assets/')) {
        continue
      }

      hints.push({
        path: 'attachments/' + file.name,
        message: `File '${file.name}' imported but not referenced in any note`,
      })
    }
  }

  // Flatten folders and notes into a single items array
  const items: ImportItem[] = [
    // Add folder items
    ...[...folders].map(path => ({ kind: 'folder' as const, path })),
    // Add note items
    ...notes.map(note => ({ kind: 'note' as const, note }))
  ]

  return {
    items,
    warnings,
    hints
  }
}
